import type { SignCheckReason } from './public-ledger-sign-check.js';

// The PUBLIC_LEDGER team's "A public ledger signature didn't match what was shown" card reads everything from the job
// metadata public-ledger/report-sign-mismatch writes: the team can't read the reporting company's rows. It holds ids, names
// and the reason code, never transaction bytes or keys.

type Metadata = ReadonlyArray<{ key?: string | null; value?: string | null } | null | undefined> | null | undefined;

// What each reason code means, for the team
const REASON_TEXT: Record<SignCheckReason, string> = {
  MALFORMED: "What nodeStream served to sign couldn't be read.",
  NOT_SCHEDULE_SIGN: "What nodeStream served to sign wasn't a signature of a scheduled transaction.",
  NOT_CANONICAL: "What nodeStream served to sign held data the browser couldn't account for.",
  SIGNED_ALREADY: 'What nodeStream served to sign already carried a signature.',
  SCHEDULE_MISMATCH: 'What nodeStream served to sign was for another scheduled transaction.',
  PAYER_MISMATCH: "What nodeStream served to sign wasn't paid for by the company's operating account.",
  FEE_TOO_HIGH: 'What nodeStream served to sign allowed a fee over the limit.',
  DURATION_TOO_LONG: 'What nodeStream served to sign stayed valid for longer than 180 seconds.',
  MEMO_NOT_EMPTY: 'The transaction carried a memo.',
  EXTRA_FIELDS: 'The transaction set more than it needs to.',
  CREATE_MISMATCH: "The stored scheduled transaction isn't the one nodeStream made for this request.",
  CONTENT_TYPE_MISMATCH: 'The scheduled transaction is a different kind from the one the card shows.',
  ACCOUNT_MISMATCH: "The signatory change is for an account other than the company's.",
  KEY_MISMATCH: "The new signing key doesn't match the proposed signatory rule.",
  TOPIC_MISMATCH: "The message goes to a topic other than the card's.",
  MESSAGE_MISMATCH: "The message text doesn't match the one the card shows.",
  UNCHECKED_TYPE: "This kind of transaction can't be checked yet, so the browser won't sign it.",
  MIRROR_MISMATCH:
    "The public ledger's own record of the scheduled transaction doesn't match the one nodeStream made for this request.",
};

export interface SignMismatchReporter {
  personId: string;
  name: string | null;
}

export interface SignMismatchReport {
  signingWorkflowId: string | null;
  companyId: string | null;
  companyName: string | null;
  transactionId: string | null;
  transactionType: string | null;
  reason: string | null;
  // What the reason means, or null for a code this client doesn't know
  reasonText: string | null;
  // Everyone whose browser refused it, first reporter first
  reportedBy: SignMismatchReporter[];
}

const values = (metadata: Metadata, key: string) =>
  (metadata ?? [])
    .filter((entry) => entry?.key === key)
    .map((entry) => (typeof entry?.value === 'string' && entry.value ? entry.value : null));

const first = (metadata: Metadata, key: string) => values(metadata, key)[0] ?? null;

// The card's figures. A later reporter is a further REPORTED_BY entry, with its REPORTED_BY_NAME at the same position
export function signMismatchReport(metadata: Metadata): SignMismatchReport {
  const reason = first(metadata, 'REASON');
  const names = values(metadata, 'REPORTED_BY_NAME');
  const reportedBy: SignMismatchReporter[] = [];
  values(metadata, 'REPORTED_BY').forEach((personId, i) => {
    if (personId && !reportedBy.some((r) => r.personId === personId))
      reportedBy.push({ personId, name: names[i] ?? null });
  });
  return {
    signingWorkflowId: first(metadata, 'SIGNING_WORKFLOW_ID'),
    companyId: first(metadata, 'COMPANY_ID'),
    companyName: first(metadata, 'COMPANY_NAME'),
    transactionId: first(metadata, 'TRANSACTION_ID'),
    transactionType: first(metadata, 'TRANSACTION_TYPE'),
    reason,
    reasonText: reason && reason in REASON_TEXT ? REASON_TEXT[reason as SignCheckReason] : null,
    reportedBy,
  };
}
